import { type UseQueryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { ItemStatusApi } from '@/common/api/itemStatus';
import type { ItemStatus } from '@/common/api/types/itemStatus';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { associateById } from '@/common/utils/associateById';

export const useGetItemStatusByIdQuery = ({
  id,
  options = {},
}: {
  id: number;
  options?: Partial<UseQueryOptions<ItemStatus, Error, ItemStatus>>;
}) => {
  return useQuery({
    queryKey: commonQueryKeys.itemStatuses.detail(id),
    queryFn: () => ItemStatusApi.getItemStatusById(id),
    ...options,
  });
};

export const getItemStatusesQueryOptions = ({
  includeArchived = false,
}: {
  includeArchived?: boolean;
} = {}) => {
  return {
    queryKey: [...commonQueryKeys.itemStatuses.all(), { includeArchived }],
    queryFn: () => ItemStatusApi.getItemStatuses({ includeArchived }),
  };
};

export const useSuspenseGetItemStatusesQuery = ({
  includeArchived = false,
  ...options
}: { includeArchived?: boolean } & Partial<
  Omit<UseQueryOptions<ItemStatus[]>, 'queryKey' | 'queryFn'>
> = {}) => {
  return useSuspenseQuery<ItemStatus[]>({
    ...getItemStatusesQueryOptions({ includeArchived }),
    ...options,
  });
};

export const useGetItemStatusesQuery = (options: Partial<UseQueryOptions<ItemStatus[]>> = {}) => {
  return useQuery({
    ...getItemStatusesQueryOptions(),
    ...options,
  });
};

export const useGetItemStatusesByIdQuery = (
  options: Partial<UseQueryOptions<ItemStatus[]>> = {},
) => {
  return useQuery({
    ...getItemStatusesQueryOptions(),
    ...options,
    select: associateById,
  });
};
